import React from 'react';

import ReactDOM from 'react-dom/client';
import { type IntlShape, RawIntlProvider } from 'react-intl-next';

import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { EditorCommand, EditorInjectionAPI } from '@atlaskit/editor-common/types';
import {
	type EditorState,
	NodeSelection,
	PluginKey,
	type Transaction,
} from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet, type EditorView } from '@atlaskit/editor-prosemirror/view';

import type { CompanyHubSectionCtaPlugin as CompanyHubSectionCtaPluginType } from './companyHubSectionCtaPluginType';
import { AnchorRectCache } from './ui/anchor-utils';
import { PlusButton } from './ui/plus-button';
import { newSectionNode } from './ui/section-node';

type PluginState = DecorationSet;
export const companyHubSectionCtaPluginKey: PluginKey<DecorationSet> = new PluginKey<PluginState>(
	'companyHubSectionCta',
);

export const isValidInsertPosition = (state: EditorState, pos: number): boolean => {
	const { schema, doc } = state;
	const $pos = doc.resolve(pos);

	const isParentDoc = $pos.parent.type === schema.nodes.doc;
	if (!isParentDoc) {
		return false;
	}

	const nodeBefore = $pos.nodeBefore;
	// The button should only appear at the start of the document or after section extensions.
	const isAtStart = !nodeBefore;
	const isAfterSectionExtension =
		nodeBefore?.attrs.extensionKey === 'hub-section' &&
		nodeBefore?.attrs.extensionType === 'com.atlassian.confluence.hub-section';

	// The button should also appear at the very end of the document.
	const isAtEnd = !$pos.nodeAfter;

	return (
		(isAtStart || isAfterSectionExtension || isAtEnd) &&
		$pos.parent.canReplaceWith($pos.index(), $pos.index(), schema.nodes.multiBodiedExtension)
	);
};

const createAnchorDecorations = (state: EditorState): DecorationSet => {
	const decs: Decoration[] = [];
	const { doc } = state;

	// A map of valid insertion positions to node info.
	const insertPositions = new Map<number, { isLastPosition: boolean; prevNodeTypeName?: string }>();

	// 1. Check for an insertion point at the very start of the document.
	if (isValidInsertPosition(state, 0)) {
		// It's the last position if the document is empty.
		const isLastPosition = doc.childCount === 0;
		insertPositions.set(0, { prevNodeTypeName: undefined, isLastPosition });
	}

	// 2. Check for insertion points after each node in the document.
	doc.content.forEach((node, offset, index) => {
		const insertPos = offset + node.nodeSize;
		if (isValidInsertPosition(state, insertPos)) {
			const isLastPosition = index === doc.childCount - 1;
			insertPositions.set(insertPos, { prevNodeTypeName: node.type.name, isLastPosition });
		}
	});

	// 3. Create decorations for all collected valid positions.
	insertPositions.forEach(({ prevNodeTypeName, isLastPosition }, insertPos) => {
		const anchorId = `section-cta-anchor-${insertPos}`;
		decs.push(
			Decoration.widget(
				insertPos,
				() => {
					const anchorEl = document.createElement('span');
					anchorEl.style.cssText = `position: absolute; anchor-name: --${anchorId};`;
					anchorEl.setAttribute('data-section-cta-anchor-name', anchorId);
					return anchorEl;
				},
				{
					key: anchorId,
					side: -1,
					anchorId: anchorId,
					prevNodeTypeName: prevNodeTypeName,
					isLastPosition: isLastPosition,
				},
			),
		);
	});

	return DecorationSet.create(doc, decs);
};

class SectionCtaPluginView {
	private view: EditorView;
	private container: HTMLDivElement;
	private root: ReactDOM.Root;
	private api: EditorInjectionAPI<any, any> | undefined;
	private intl: IntlShape;
	private anchorRectCache: AnchorRectCache;

	constructor(view: EditorView, api: EditorInjectionAPI<any, any> | undefined, intl: IntlShape) {
		this.view = view;
		this.api = api;
		this.intl = intl;
		this.anchorRectCache = new AnchorRectCache();
		this.anchorRectCache.setEditorView(this.view);

		this.container = document.createElement('div');
		this.view.dom.parentElement?.appendChild(this.container);
		this.root = ReactDOM.createRoot(this.container);

		this.renderButtons(this.view.state);
	}

	update(view: EditorView, prevState: EditorState) {
		this.view = view;
		this.anchorRectCache.setEditorView(this.view);
		// We re-render only if the decorations have changed - i.e. the user has modified top level nodes
		const oldState = companyHubSectionCtaPluginKey.getState(prevState);
		const newState = companyHubSectionCtaPluginKey.getState(view.state);
		if (oldState !== newState) {
			this.anchorRectCache.clear();
			this.renderButtons(view.state);
		}
	}

	destroy() {
		this.anchorRectCache.clear();
		this.root.unmount();
		this.container.remove();
	}

	private renderButtons(state: EditorState) {
		const decorationSet = companyHubSectionCtaPluginKey.getState(state);
		if (!decorationSet) {
			return;
		}

		const handleClick = (insertPos: number) => {
			const command = this.api?.companyHubSectionCtaPlugin?.commands?.insertSection(
				insertPos,
				this.intl,
			);
			if (command) {
				command(this.view.state, this.view.dispatch, this.view);
			}
		};

		// Find all our anchor decorations
		const anchorDecos = decorationSet.find();

		this.root.render(
			<RawIntlProvider value={this.intl}>
				{anchorDecos.map((deco) => {
					const anchorId = deco.spec.anchorId;
					const insertPos = deco.from;
					const prevNodeTypeName = deco.spec.prevNodeTypeName;
					const isLastPosition = deco.spec.isLastPosition;
					return (
						<PlusButton
							key={anchorId}
							anchorId={anchorId}
							prevNodeTypeName={prevNodeTypeName}
							isLastPosition={isLastPosition}
							onClick={() => handleClick(insertPos)}
							anchorRectCache={this.anchorRectCache}
							view={this.view}
						/>
					);
				})}
			</RawIntlProvider>,
		);
	}
}

export const companyHubSectionCtaPlugin: CompanyHubSectionCtaPluginType = ({ api }) => {
	return {
		name: 'companyHubSectionCtaPlugin',
		pmPlugins() {
			return [
				{
					name: 'companyHubSectionCtaButton',
					plugin: ({ getIntl }) => {
						const intl = getIntl();
						return new SafePlugin<PluginState>({
							key: companyHubSectionCtaPluginKey,
							state: {
								init(_, editorState) {
									return createAnchorDecorations(editorState);
								},
								apply(tr, oldDecs, newState) {
									return createAnchorDecorations(newState);
								},
							},
							props: {
								decorations(state: EditorState) {
									// This makes the anchor decorations actually appear in the doc.
									return companyHubSectionCtaPluginKey.getState(state);
								},
							},
							view: (editorView) => new SectionCtaPluginView(editorView, api, intl),
						});
					},
				},
			];
		},
		commands: {
			insertSection: (position, intl) => {
				return ((state: EditorState, dispatch?: (tr: Transaction) => void) => {
					if (dispatch) {
						const { tr } = state;
						const nodeToInsert = newSectionNode(state, intl);
						tr.insert(position, nodeToInsert);

						const selection = NodeSelection.create(tr.doc, position);
						tr.setSelection(selection);

						dispatch(tr);
					}
					return true;
				}) as unknown as EditorCommand;
			},
		},
	};
};
